import { t, plural } from '@lingui/core/macro';

// Corrections persist the instant `saving` resolves; later phases only track
// the async rebuild of the translated file.
export type LifecyclePhase = 'idle' | 'saving' | 'applying' | 'done' | 'failed';

// One object, one derivation, so the surfaces can never disagree.
export type TranslationLifecycle = {
  phase: LifecyclePhase;
  dirtyCount: number;
  // Null when there was no in-session save (a cold open), selecting the countless copy.
  appliedEditCount: number | null;
  // Keys the failed strip so fail -> retry -> fail re-announces to screen readers.
  attemptId: number;
  // Retry clicked, poll has not yet reported the new job.
  enqueueing: boolean;
};

export type LifecycleSurfaces = {
  gridLocked: boolean;
  // Header save-status slot.
  header: {
    label: string;
    tone: 'muted' | 'neutral' | 'progress' | 'success' | 'warning';
    showSpinner: boolean;
  };
  // Floating chip over the muted grid, applying only.
  chip: { visible: boolean; label: string };
  // Failed-only explanation strip; the morphing header button carries the action.
  strip: { visible: boolean; text: string; announceKey: number };
  // The one Save button, morphing to an orange Retry when failed with nothing to
  // save (Save persists and re-enqueues, so it subsumes Retry when dirty).
  button: { label: string; isRetry: boolean; disabled: boolean; showSpinner: boolean };
};

export function deriveLifecycleSurfaces(lifecycle: TranslationLifecycle): LifecycleSurfaces {
  const { phase, dirtyCount, appliedEditCount, attemptId, enqueueing } = lifecycle;
  const dirty = dirtyCount > 0;
  const isRetry = phase === 'failed' && !dirty;

  const unsavedLabel = plural(dirtyCount, { one: '# unsaved', other: '# unsaved' });

  const header: LifecycleSurfaces['header'] =
    phase === 'saving'
      ? { label: t`Saving...`, tone: 'progress', showSpinner: true }
      : phase === 'applying'
        ? { label: t`Applying your edits...`, tone: 'progress', showSpinner: true }
        : phase === 'done'
          ? {
              label:
                appliedEditCount == null
                  ? t`Edits live`
                  : plural(appliedEditCount, { one: '# edit live', other: '# edits live' }),
              tone: 'success',
              showSpinner: false,
            }
          : phase === 'failed'
            ? {
                label: dirty ? unsavedLabel : t`Translation didn't finish`,
                tone: 'warning',
                showSpinner: false,
              }
            : dirty
              ? { label: unsavedLabel, tone: 'neutral', showSpinner: false }
              : { label: t`No unsaved changes`, tone: 'muted', showSpinner: false };

  const stripText =
    appliedEditCount != null
      ? dirty
        ? plural(appliedEditCount, {
            one: "The translation didn't finish. Your # saved edit is safe. Saving will retry it.",
            other:
              "The translation didn't finish. Your # saved edits are safe. Saving will retry it.",
          })
        : plural(appliedEditCount, {
            one: "The translation didn't finish. Your # saved edit is safe. Retry when ready.",
            other: "The translation didn't finish. Your # saved edits are safe. Retry when ready.",
          })
      : dirty
        ? t`The last translation didn't finish. Saved edits are unaffected. Saving will retry it.`
        : t`The last translation didn't finish. Saved edits are unaffected.`;

  return {
    header,
    gridLocked: phase === 'applying',
    chip: { visible: phase === 'applying', label: t`Read-only while your edits are applied` },
    strip: { visible: phase === 'failed', text: stripText, announceKey: attemptId },
    button: {
      label: isRetry ? t`Retry` : t`Save`,
      isRetry,
      disabled:
        enqueueing ||
        phase === 'saving' ||
        phase === 'applying' ||
        (dirtyCount === 0 && phase !== 'failed'),
      showSpinner: phase === 'saving' || enqueueing,
    },
  };
}

export const lifecycleToneColour: Record<LifecycleSurfaces['header']['tone'], string> = {
  muted: 'gray.400',
  neutral: 'gray.400',
  progress: 'blue.500',
  success: 'green.500',
  warning: 'orange.500',
};
